import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tooltip
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { v4 as uuidv4 } from "uuid";

import PromptBuilder from "./PromptBuilder";
import { useAxiosAuth } from "../hooks/useAxiosAuth";
import { AuthContext } from "../context/AuthContext";
import { NetworkStatusContext } from "../context/NetworkStatusContext";

// Lifted out so the array reference is stable
import { BUILT_IN_TEMPLATES } from "./BuiltinTemplates";

// map server response into local template shape
function mapServerTemplate(t) {
  const { fragments: payloadFrags = [], ...meta } = t.payload || {};
  return {
    id: t.id,
    name: t.name,
    builtIn: false,
    fragments: (payloadFrags || []).map((f) => ({
      ...f,
      id: uuidv4(),
      checked: f.checked ?? true,
      scope: f.scope ?? "section",
    })),
    ...meta
  };
}

export default function TemplateManager({
  fragments,
  setFragments,
  compiledPrompt,
  templateMeta,
  setTemplateMeta,
  selectedTemplateId,
  setSelectedTemplateId,
  /** when true, hide the picker and save/delete UI */
  editOnly = false
}) {
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [dlg, setDlg] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [showCompiled, setShowCompiled] = useState(false);
  const [error, setError] = useState(null);

  const axiosInstance = useAxiosAuth();
  const { user } = useContext(AuthContext);
  const networkStatus = useContext(NetworkStatusContext);
  const isOffline = networkStatus === "offline";

  // on-mount: fetch existing templates and seed default if none
  useEffect(() => {
    if (!user) return;
    if (isOffline) {
      setError("Offline: cannot load templates.");
      return;
    }
    (async () => {
      try {
        const { data: remoteRaw } = await axiosInstance.get("/prompt_templates");
        const remote = (remoteRaw || []).map(mapServerTemplate);
        setTemplates(remote);

        // seed default if no remote templates and none selected
        if ((remoteRaw || []).length === 0 && !selectedTemplateId) {
          const defaultTpl = BUILT_IN_TEMPLATES.find(t => t.id === "default");
          setSelectedTemplateId(defaultTpl.id);
          setTplName(defaultTpl.name);
          const seeded = (defaultTpl.fragments || []).map(f => ({
            ...f,
            id: uuidv4(),
            checked: true
          }));
          setFragments(seeded);
          const { fragments: _f, id: _i, name: _n, builtIn: _b, ...meta } = defaultTpl;
          setTemplateMeta(meta);
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError("Unable to load templates. Please check your network connection.");
      }
    })();
  }, [axiosInstance, setFragments, setTemplateMeta, user, isOffline, selectedTemplateId]);

  // merge built-in + user templates
  const dropdownOptions = useMemo(
    () => [...BUILT_IN_TEMPLATES, ...templates],
    [templates]
  );

  // find selected template
  const selectedOpt = useMemo(
    () => dropdownOptions.find(t => t.id === selectedTemplateId) || dropdownOptions[0],
    [dropdownOptions, selectedTemplateId]
  );

  // keep name in sync
  useEffect(() => {
    if (selectedOpt) setTplName(selectedOpt.name);
  }, [selectedOpt]);

  // handle template selection
  const handleSelect = (_, v) => {
    if (!v) return;
    setSelectedTemplateId(v.id);
    setTplName(v.name);
    const frags = (v.fragments || []).map(f => ({ ...f, id: uuidv4(), checked: f.checked ?? true }));
    setFragments(frags);
    setTemplateMeta({
      domain: v.domain,
      sectionsPerLesson: v.sectionsPerLesson,
      readingLevel: v.readingLevel,
      tone: v.tone,
      mediaDensity: v.mediaDensity,
      codeLanguage: v.codeLanguage,
      language: v.language,
      timePerLesson: v.timePerLesson
    });
  };

  // save (or update) template
  const handleSave = async (forceNew = false) => {
    if (isOffline) { setError("Offline: cannot save template."); return; }
    const payload = { name: tplName, payload: { ...templateMeta, fragments } };

    try {
      if (forceNew || !selectedTemplateId) {
        const { data } = await axiosInstance.post("/prompt_templates", payload);
        const newTpl = mapServerTemplate(data);
        setTemplates(t => [...t, newTpl]);
        setSelectedTemplateId(newTpl.id);
      } else {
        const { data } = await axiosInstance.put(
          `/prompt_templates/${selectedTemplateId}`,
          payload
        );
        const updated = mapServerTemplate(data);
        setTemplates(t => t.map(x => x.id === updated.id ? updated : x));
      }
      setDlg(null);
    } catch (err) {
      console.error("Error saving template:", err);
      setError("Unable to save template. Please try again later.");
    }
  };

  // delete template
  const handleDelete = async () => {
    if (!selectedTemplateId) return;
    try {
      await axiosInstance.delete(`/prompt_templates/${selectedTemplateId}`);
      setTemplates(t => t.filter(x => x.id !== selectedTemplateId));
      setSelectedTemplateId(null);
      setDlg(null);
    } catch (err) {
      console.error("Error deleting template:", err);
      setError("Unable to delete template. Please check your connection.");
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      {!editOnly && (
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          options={dropdownOptions}
          getOptionLabel={opt => opt.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          value={selectedOpt}
          onChange={handleSelect}
          renderInput={params => <TextField {...params} label="Lesson Content Style" />}
        />
      )}

      {!editOnly && (
        <>
          <IconButton onClick={() => setDlg("save")} disabled={selectedOpt.builtIn}><SaveIcon /></IconButton>
          <IconButton onClick={() => setDlg("saveAs")}><SaveAsIcon /></IconButton>
          <IconButton onClick={() => setDlg("delete")} disabled={selectedOpt.builtIn}><DeleteIcon /></IconButton>
        </>
      )}

      <IconButton onClick={() => setEditOpen(true)}><EditIcon /></IconButton>
      <Tooltip title="View Compiled Prompt">
        <IconButton onClick={() => setShowCompiled(true)}><InfoOutlinedIcon /></IconButton>
      </Tooltip>

      {/* Fragment / Meta editor dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Lesson Structure</DialogTitle>
        <DialogContent dividers>
          <PromptBuilder
            fragments={fragments}
            setFragments={setFragments}
            templateMeta={templateMeta}
            setTemplateMeta={setTemplateMeta}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setEditOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Compiled prompt viewer */}
      <Dialog open={showCompiled} onClose={() => setShowCompiled(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compiled Prompt</DialogTitle>
        <DialogContent dividers>
          <Typography component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {compiledPrompt}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompiled(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save/SaveAs/Delete confirmation dialogs */}
      <Dialog open={dlg !== null} onClose={() => setDlg(null)}>
        <DialogTitle>{dlg === "saveAs" ? "Save As New Template" : dlg === "save" ? "Save Template" : "Delete Template"}</DialogTitle>
        <DialogContent dividers>
          {(dlg === "save" || dlg === "saveAs") && (
            <TextField
              fullWidth
              label="Template Name"
              value={tplName}
              onChange={e => setTplName(e.target.value)}
            />
          )}
          {dlg === "delete" && <Typography>Are you sure you want to delete this template?</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancel</Button>
          {(dlg === "save" || dlg === "saveAs") && (<Button onClick={() => handleSave(dlg === "saveAs")}>OK</Button>)}
          {dlg === "delete" && (<Button onClick={handleDelete} color="error">Delete</Button>)}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
